import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Calendar } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = 'https://uandfashion.eu';

const formatDate = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang === 'nl' ? 'nl-BE' : 'en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/posts/${slug}`)
      .then((res) => {
        if (!cancelled) {
          setPost(res.data);
          setStatus('ok');
        }
      })
      .catch((err) => {
        if (!cancelled) setStatus(err?.response?.status === 404 ? 'notfound' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (status === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (status !== 'ok') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-3xl font-light mb-3">{t('Artikel niet gevonden', 'Article not found')}</h1>
          <Link to="/inspiratie" className="inline-block bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-800">
            {t('Bekijk alle artikels', 'Browse all articles')}
          </Link>
        </div>
      </div>
    );
  }

  const title = language === 'nl' ? post.title_nl : (post.title_en || post.title_nl);
  const body = language === 'nl' ? post.body_nl : (post.body_en || post.body_nl);
  const excerpt = language === 'nl'
    ? (post.excerpt_nl || post.excerpt_en || '')
    : (post.excerpt_en || post.excerpt_nl || '');

  return (
    <div className="bg-white" data-testid="blog-post-page">
      <SEO
        title={`${title} — U&Fashion`}
        description={excerpt || title}
        canonical={`${SITE_URL}/inspiratie/${post.slug}`}
        ogImage={post.hero_image}
      />
      <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-10 pb-4">
        <Link to="/inspiratie" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-pink-600">
          <ArrowLeft className="w-4 h-4" />
          {t('Alle artikels', 'All articles')}
        </Link>
      </div>
      <article className="max-w-3xl mx-auto px-6 sm:px-8 pb-20">
        <div className="rounded-3xl overflow-hidden mb-10 shadow-sm bg-gray-100">
          <img src={post.hero_image} alt={title} className="w-full h-auto max-h-[80vh] object-cover object-top" loading="eager" />
        </div>
        <header className="mb-10">
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(post.created_at, language)}
            {post.tags?.length > 0 && (
              <span className="ml-2 flex gap-1.5">
                {post.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="text-[10px] uppercase tracking-wider bg-pink-50 text-pink-600 px-2 py-0.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </span>
            )}
          </div>
          <h1 className="text-4xl sm:text-5xl font-light text-gray-900 leading-tight tracking-tight mb-6">
            {title}
          </h1>
          {excerpt && <p className="text-lg text-gray-600 leading-relaxed">{excerpt}</p>}
        </header>
        <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed">
          <ReactMarkdown
            components={{
              h2: ({ node, ...props }) => (
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mt-10 mb-4 tracking-tight" {...props} />
              ),
              h3: ({ node, ...props }) => (
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 mb-3 tracking-tight" {...props} />
              ),
              p: ({ node, ...props }) => (
                <p className="mb-5 leading-relaxed" {...props} />
              ),
              strong: ({ node, ...props }) => (
                <strong className="font-semibold text-gray-900" {...props} />
              ),
              ul: ({ node, ...props }) => (
                <ul className="list-disc pl-6 mb-5 space-y-2" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="list-decimal pl-6 mb-5 space-y-2" {...props} />
              ),
              a: ({ node, ...props }) => {
                const href = props.href || '';
                const internal = href.startsWith('/') || href.startsWith('#');
                if (internal) {
                  return (
                    <Link to={href} className="inline-flex items-center gap-1.5 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold px-6 py-3 rounded-full transition-colors no-underline my-4">
                      {props.children}
                    </Link>
                  );
                }
                return <a className="text-pink-600 underline hover:text-pink-700" {...props} />;
              },
              img: ({ node, ...props }) => {
                // Portrait product shots use `![alt](url "product")` in
                // markdown. We detect the "product" title and render a
                // smaller, centered card instead of a full-width photo so
                // it doesn't dominate the personal-voice narrative.
                if (props.title === 'product') {
                  return (
                    <span className="block my-8 mx-auto max-w-sm">
                      <img
                        className="rounded-2xl w-full h-auto shadow-md"
                        loading="lazy"
                        {...props}
                      />
                    </span>
                  );
                }
                return (
                  <img
                    className="rounded-2xl w-full h-auto my-8 shadow-md"
                    loading="lazy"
                    {...props}
                  />
                );
              },
            }}
          >
            {body}
          </ReactMarkdown>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
